import { useState } from 'react'
import { Alert, Box, Button, Chip, Divider, IconButton, Sheet, Snackbar, Stack, Tab, TabList, TabPanel, Tabs, Typography } from '@mui/joy'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import { getMcpEndpointUrl, getMcpOAuthBaseUrl } from 'api/oracleApi'
import { useMintMcpToken } from '../hooks/useMintMcpToken'

const SERVER_NAME = 'numeo-oracle'

// Tabs that need a Bearer token (CLI / programmatic clients)
const BEARER_TABS = [2, 3]

function CodeBlock({ code, onCopy }: { code: string; onCopy: (text: string) => void }) {
    return (
        <Sheet variant="soft" sx={{ borderRadius: 'sm', p: 1.5, pr: 5, position: 'relative', bgcolor: 'background.level1' }}>
            <Typography level="body-sm" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {code}
            </Typography>
            <IconButton size="sm" variant="plain" sx={{ position: 'absolute', top: 6, right: 6 }} onClick={() => onCopy(code)} aria-label="Copy">
                <ContentCopyIcon fontSize="small" />
            </IconButton>
        </Sheet>
    )
}

export function McpAccessPanel() {
    const { result, loading, error, mint } = useMintMcpToken()
    const [snackOpen, setSnackOpen] = useState(false)
    const [connector, setConnector] = useState(0)

    const mcpUrl = getMcpEndpointUrl()
    const oauthBase = getMcpOAuthBaseUrl()
    const token = result?.token
    const tokenForSnippet = token ?? '<YOUR_TOKEN>'
    const needsToken = BEARER_TABS.includes(connector)

    const copy = (text: string) => {
        navigator.clipboard.writeText(text)
        setSnackOpen(true)
    }

    const claudeCodeCmd = `claude mcp add --transport http ${SERVER_NAME} ${mcpUrl} \\\n  --header "Authorization: Bearer ${tokenForSnippet}"`
    const manusJson = JSON.stringify(
        { mcpServers: { [SERVER_NAME]: { url: mcpUrl, transport: 'http', headers: { Authorization: `Bearer ${tokenForSnippet}` } } } },
        null,
        2
    )
    const expiresLabel = result ? new Date(result.expiresAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : ''

    return (
        <Stack gap={2} sx={{ maxWidth: 820 }}>
            <Stack gap={0.5}>
                <Typography level="title-lg" startDecorator={<VpnKeyIcon />}>
                    MCP Access
                </Typography>
                <Typography level="body-sm" color="neutral">
                    Connect Claude, Manus, or Perplexity to the Numeo docs knowledge base (ADRs &amp; PRDs) over MCP.
                </Typography>
            </Stack>

            {/* Bearer token — only shown for CLI/programmatic clients (Claude Code, Manus) */}
            {needsToken && (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 2 }}>
                    <Stack gap={1.5}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                            <Typography level="title-sm">Personal token</Typography>
                            {result && (
                                <Chip size="sm" variant="soft" color="success">
                                    Expires {expiresLabel}
                                </Chip>
                            )}
                        </Stack>

                        {error && <Alert color="danger">{error}</Alert>}

                        {!result && (
                            <>
                                <Typography level="body-sm" color="neutral">
                                    The token is tied to your account and inherits your access. It is shown once — copy it before leaving this page.
                                </Typography>
                                <Button startDecorator={<VpnKeyIcon />} loading={loading} onClick={mint} sx={{ alignSelf: 'flex-start' }}>
                                    Generate MCP token
                                </Button>
                            </>
                        )}

                        {result && (
                            <>
                                <Alert color="warning" startDecorator={<WarningAmberIcon />} variant="soft">
                                    Copy this token now — it is shown only once and grants access as you. Treat it like a password. Regenerating invalidates
                                    nothing on its own; the previous token keeps working until it expires.
                                </Alert>
                                <CodeBlock code={token as string} onCopy={copy} />
                                <Button
                                    size="sm"
                                    variant="outlined"
                                    color="neutral"
                                    startDecorator={<AutorenewIcon />}
                                    loading={loading}
                                    onClick={mint}
                                    sx={{ alignSelf: 'flex-start' }}
                                >
                                    Generate a new token
                                </Button>
                            </>
                        )}
                    </Stack>
                </Sheet>
            )}

            <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 2 }}>
                <Stack gap={1.5}>
                    <Typography level="title-sm">Connect your client</Typography>
                    <Tabs value={connector} onChange={(_, v) => setConnector(v as number)}>
                        <TabList>
                            <Tab>Claude.ai</Tab>
                            <Tab>Perplexity</Tab>
                            <Tab>Claude Code</Tab>
                            <Tab>Manus</Tab>
                        </TabList>

                        {/* Claude.ai — OAuth, no token needed */}
                        <TabPanel value={0} sx={{ px: 0 }}>
                            <Stack gap={1.5}>
                                <Typography level="body-sm">
                                    Go to{' '}
                                    <Box component="span" sx={{ fontFamily: 'monospace' }}>
                                        claude.ai → Settings → Connectors → Add custom connector
                                    </Box>
                                    . Paste the URL below — OAuth login is handled automatically, no token needed.
                                </Typography>
                                <CodeBlock code={oauthBase} onCopy={copy} />
                                <Stack direction="row" gap={1}>
                                    <Chip size="sm" variant="soft" color="primary">
                                        OAuth 2.0
                                    </Chip>
                                    <Chip size="sm" variant="soft" color="neutral">
                                        Streamable HTTP
                                    </Chip>
                                </Stack>
                            </Stack>
                        </TabPanel>

                        {/* Perplexity — OAuth, no token needed */}
                        <TabPanel value={1} sx={{ px: 0 }}>
                            <Stack gap={1.5}>
                                <Typography level="body-sm">
                                    Go to{' '}
                                    <Box component="span" sx={{ fontFamily: 'monospace' }}>
                                        Settings → Connectors → Add Connector → Custom
                                    </Box>
                                    . Paste the URL below, select <strong>Streamable HTTP</strong> transport and <strong>OAuth</strong> authentication — you
                                    will be prompted to sign in with your Google account.
                                </Typography>
                                <CodeBlock code={oauthBase} onCopy={copy} />
                                <Stack direction="row" gap={1}>
                                    <Chip size="sm" variant="soft" color="primary">
                                        OAuth 2.0
                                    </Chip>
                                    <Chip size="sm" variant="soft" color="neutral">
                                        Streamable HTTP
                                    </Chip>
                                </Stack>
                            </Stack>
                        </TabPanel>

                        {/* Claude Code CLI — Bearer token */}
                        <TabPanel value={2} sx={{ px: 0 }}>
                            <Stack gap={1.5}>
                                <Typography level="body-sm">Run in your terminal. Generate a personal token above first.</Typography>
                                <CodeBlock code={claudeCodeCmd} onCopy={copy} />
                                <Divider />
                                <Typography level="body-xs" color="neutral">
                                    Or add as a remote server in Claude Desktop (
                                    <Box component="span" sx={{ fontFamily: 'monospace' }}>
                                        Settings → Developer → Edit Config
                                    </Box>
                                    ):
                                </Typography>
                                <CodeBlock
                                    code={JSON.stringify(
                                        {
                                            mcpServers: {
                                                [SERVER_NAME]: {
                                                    type: 'streamable-http',
                                                    url: mcpUrl,
                                                    headers: { Authorization: `Bearer ${tokenForSnippet}` },
                                                },
                                            },
                                        },
                                        null,
                                        2
                                    )}
                                    onCopy={copy}
                                />
                            </Stack>
                        </TabPanel>

                        {/* Manus — Bearer token */}
                        <TabPanel value={3} sx={{ px: 0 }}>
                            <Stack gap={1.5}>
                                <Typography level="body-sm">Settings → Custom MCP Servers → Import by JSON. Generate a personal token above first.</Typography>
                                <CodeBlock code={manusJson} onCopy={copy} />
                            </Stack>
                        </TabPanel>
                    </Tabs>
                </Stack>
            </Sheet>

            <Snackbar open={snackOpen} onClose={() => setSnackOpen(false)} autoHideDuration={2000} color="success" variant="soft">
                Copied to clipboard
            </Snackbar>
        </Stack>
    )
}
